/* ========================================================================
   Formulaire de précommande (produits épuisés)

   Envoie nom, prénom, téléphone et adresse à un Google Apps Script, qui
   consigne la demande dans une feuille de calcul. Aucune commande Shopify
   n'est créée : le parcours d'achat normal (EasySell COD) reste réservé aux
   produits en stock.
   ======================================================================== */

(() => {
  // Le snippet injecte le script à chaque rendu du bloc ; on ne l'initialise
  // qu'une fois pour éviter des écouteurs et des envois en double.
  if (window.__shopiwebPreorderInit) return;
  window.__shopiwebPreorderInit = true;

  const FIELD_LABELS = {
    first_name: "prénom",
    last_name: "nom",
    phone: "téléphone",
    address: "adresse",
  };

  // Le contenu vient de réglages marchand : on l'échappe avant de l'injecter.
  const escapeHtml = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (char) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[char]
    );

  const showStatus = (block, message, isError) => {
    const status = block.querySelector("[data-preorder-status]");
    if (!status) return;

    status.innerHTML = `
      <div class="preorder__alert alert alert-${isError ? "danger" : "success"} mb-0" role="alert">
        ${escapeHtml(message)}
      </div>
    `;
  };

  // Remplace le formulaire par une confirmation, plutôt qu'un simple bandeau :
  // la précommande étant sans paiement, l'état final doit être sans ambiguïté.
  const showSuccess = (block) => {
    const fields = block.querySelector("[data-preorder-fields]");
    const status = block.querySelector("[data-preorder-status]");
    if (fields) fields.hidden = true;
    if (!status) return;

    const title = block.dataset.successTitle || "Précommande enregistrée";
    const message =
      block.dataset.successMessage ||
      "Nous vous contactons dès le retour en stock.";

    status.innerHTML = `
      <div class="preorder__done">
        <span class="preorder__done-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6 9 17l-5-5"></path>
          </svg>
        </span>
        <span class="fw-semibold">${escapeHtml(title)}</span>
        <span class="small opacity-75">${escapeHtml(message)}</span>
      </div>
    `;
  };

  const clearStatus = (block) => {
    const status = block.querySelector("[data-preorder-status]");
    if (status) status.innerHTML = "";
  };

  const collectValues = (block) => {
    const values = {};
    block.querySelectorAll("[data-preorder-field]").forEach((input) => {
      values[input.dataset.preorderField] = input.value.trim();
    });
    return values;
  };

  // Validation côté client : les champs ne sont pas dans un <form>, la
  // validation native du navigateur ne se déclenche donc jamais.
  const findMissingField = (block, values) => {
    return Object.keys(FIELD_LABELS).find((key) => !values[key]);
  };

  const setInvalid = (block, fieldName) => {
    block.querySelectorAll("[data-preorder-field]").forEach((input) => {
      input.classList.toggle(
        "is-invalid",
        input.dataset.preorderField === fieldName
      );
    });
    const target = block.querySelector(
      `[data-preorder-field="${fieldName}"]`
    );
    if (target) target.focus();
  };

  const resetButton = (button) => {
    if (!button) return;
    button.disabled = false;
    button.style.width = "";
    button.textContent = button.dataset.textDefault;
  };

  const clearInvalid = (block) => {
    block
      .querySelectorAll("[data-preorder-field]")
      .forEach((input) => input.classList.remove("is-invalid"));
  };

  const submitPreorder = async (block, button) => {
    const endpoint = block.dataset.endpoint;

    if (!endpoint) {
      // Réglage non configuré : on le signale plutôt que d'échouer en silence.
      showStatus(
        block,
        "Le formulaire de précommande n'est pas encore configuré.",
        true
      );
      return;
    }

    const values = collectValues(block);
    const missing = findMissingField(block, values);

    if (missing) {
      clearStatus(block);
      setInvalid(block, missing);
      showStatus(block, `Merci d'indiquer votre ${FIELD_LABELS[missing]}.`, true);
      return;
    }

    // Robot détecté : on simule un succès pour ne pas l'informer du blocage.
    if (block.querySelector("[data-preorder-honeypot]")?.value) {
      showSuccess(block);
      return;
    }

    clearInvalid(block);
    clearStatus(block);

    // La largeur est figée avant de vider le bouton, sinon il se rétracte
    // autour du spinner et la mise en page sursaute.
    button.style.width = `${button.offsetWidth}px`;
    button.disabled = true;
    button.innerHTML = `
      <span class="spinner-border spinner-border-sm align-middle me-2" role="status" aria-hidden="true"></span>
      ${escapeHtml(button.dataset.textSending)}
    `;

    const payload = {
      ...values,
      product_title: block.dataset.productTitle,
      product_id: block.dataset.productId,
      variant_id: block.dataset.variantId,
      variant_title: block.dataset.variantTitle,
      shop: block.dataset.shop,
      page_url: window.location.href,
      submitted_at: new Date().toISOString(),
    };

    try {
      // Google Apps Script ne renvoie pas d'en-têtes CORS : "no-cors" permet
      // l'envoi, au prix d'une réponse opaque. On ne peut donc pas lire le
      // statut renvoyé — seule une panne réseau est détectable ici.
      // text/plain évite la requête preflight, que le script ne gère pas.
      await fetch(endpoint, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify(payload),
      });

      showSuccess(block);
    } catch (error) {
      console.error("Précommande :", error);
      resetButton(button);
      showStatus(
        block,
        "L'envoi a échoué. Vérifiez votre connexion et réessayez.",
        true
      );
    }
  };

  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-preorder-submit]");
    if (!button) return;

    const block = button.closest("[data-preorder-block]");
    if (block) submitPreorder(block, button);
  });

  // Quand la précommande est proposée, le bouton « Ajouter au panier » (grisé,
  // inutilisable) et les boutons de paiement rapide sont masqués : deux appels
  // à l'action contradictoires feraient croire à une erreur du site.
  //
  // Le bouton EasySell n'est PAS touché : l'app l'injecte et le rebinde
  // elle-même, et il ne s'affiche de toute façon pas sur une variante épuisée.
  const BUY_SELECTORS = [
    ".product-buy-buttons .btn-atc",
    ".product-buy-buttons .quantity-wrapper",
    ".product-form-dynamic-buttons",
  ];

  const toggleBuyButtons = (hide) => {
    document.querySelectorAll(BUY_SELECTORS.join(",")).forEach((el) => {
      // On mémorise l'état d'origine pour ne pas ré-afficher un élément que le
      // thème masquait déjà pour une autre raison.
      if (hide) {
        if (el.dataset.preorderPrevHidden === undefined) {
          el.dataset.preorderPrevHidden = el.hidden ? "1" : "0";
        }
        el.hidden = true;
      } else if (el.dataset.preorderPrevHidden !== undefined) {
        el.hidden = el.dataset.preorderPrevHidden === "1";
        delete el.dataset.preorderPrevHidden;
      }
    });
  };

  // Les champs de précommande vivent dans le {% form %} produit. « hidden »
  // ne les exclut PAS de la validation HTML5 : seul « disabled » le fait.
  // Sans ça, un produit en stock ne peut pas être ajouté au panier (le
  // navigateur bloque l'envoi sur des champs requis invisibles).
  const setFieldsDisabled = (block, disabled) => {
    block
      .querySelectorAll("[data-preorder-field]")
      .forEach((field) => {
        field.disabled = disabled;
      });
  };

  const applyVariantState = (variant) => {
    const blocks = document.querySelectorAll("[data-preorder-block]");
    if (!blocks.length) return;

    blocks.forEach((block) => {
      block.hidden = variant.available;
      setFieldsDisabled(block, variant.available);
      block.dataset.variantId = variant.id;
      block.dataset.variantTitle = variant.title;

      // Nouvelle variante : on repart d'un formulaire vierge.
      const fields = block.querySelector("[data-preorder-fields]");
      if (fields) fields.hidden = false;
      clearStatus(block);
      clearInvalid(block);

      resetButton(block.querySelector("[data-preorder-submit]"));
    });

    toggleBuyButtons(!variant.available);
  };

  // Le formulaire suit la variante sélectionnée : visible uniquement quand la
  // variante courante est épuisée. Couvre le cas d'un produit disponible dont
  // seule une couleur est en rupture.
  window.addEventListener("variantChange.shopiweb.product", (event) => {
    if (event.detail) applyVariantState(event.detail);
  });

  // Cas du chargement direct sur une variante épuisée (lien partagé, retour
  // arrière) : aucun événement de changement n'est émis, on aligne donc l'état
  // des boutons sur ce que Liquid a rendu.
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-preorder-block]").forEach((block) => {
      // Filet de sécurité : l'état « disabled » doit toujours suivre
      // « hidden », même si le rendu Liquid a été mis en cache.
      setFieldsDisabled(block, block.hidden);
      if (!block.hidden) toggleBuyButtons(true);
    });
  });
})();
